import React, {useEffect, useState} from 'react';
import {Box, Typography} from '@material-ui/core';
import {Skeleton} from '@material-ui/lab';

import {apiGetProgramSummary} from 'constants/Api';
import {IProgramSummary} from 'constants/interfaces';

import './ProgramSummaryWidget.scss';

export default function ProgramSummaryWidget(): React.ReactElement {
  const [summary, setSummary] = useState<IProgramSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);

    apiGetProgramSummary()
      .then((res) => {
        if (alive) setSummary(res);
      })
      .catch((err) => {
        console.error('Failed to load program summary for widget', err);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });

    return () => {
      alive = false;
    };
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  if (loading) {
    return <Skeleton variant="rect" height={280} style={{borderRadius: 8}} />;
  }

  if (!summary) {
    return (
      <Box className="program-summary-widget">
        <Typography className="widget-heading">PROGRAM SUMMARY</Typography>
        <Typography variant="body2" color="textSecondary">
          Could not load program data.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="program-summary-widget">
      <Typography className="widget-heading">PROGRAM SUMMARY</Typography>

      <Box className="metrics-row">
        <Box className="metric-card">
          <Typography className="metric-value">
            {summary.totalCount.toLocaleString()}
          </Typography>
          <Typography className="metric-label">Total Programs</Typography>
        </Box>
        <Box className="metric-card">
          <Typography className="metric-value">
            {formatCurrency(summary.totalValue)}
          </Typography>
          <Typography className="metric-label">Total Value</Typography>
        </Box>
      </Box>

      <Box className="breakdowns">
        {summary.typeBreakdown?.length > 0 && (
          <Box className="breakdown-section">
            <Typography className="section-title">By Type</Typography>
            {summary.typeBreakdown.map((tb, idx) => (
              <Box key={idx} className="breakdown-row">
                <Typography className="name">{tb.type || 'Unknown'}</Typography>
                <Typography className="count">{tb.count}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {summary.statusBreakdown?.length > 0 && (
          <Box className="breakdown-section">
            <Typography className="section-title">By Status</Typography>
            {summary.statusBreakdown.map((sb, idx) => (
              <Box key={idx} className="breakdown-row">
                <Typography className="name">
                  {sb.status || 'Unknown'}
                </Typography>
                <Typography className="count">{sb.count}</Typography>
              </Box>
            ))}
          </Box>
        )}

        {summary.cropBreakdown?.length > 0 && (
          <Box className="breakdown-section">
            <Typography className="section-title">By Crop</Typography>
            {summary.cropBreakdown.map((cb, idx) => (
              <Box key={idx} className="breakdown-row">
                <Typography className="name">{cb.crop || 'Unknown'}</Typography>
                <Typography className="count">{cb.count}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
